import { Paper, Text, SimpleGrid, Skeleton, Group, Stack } from '@mantine/core';
import type { MarketStats } from '../types';
import type { ShipmentFilter } from '../hooks/useShipments';
import type { DateRangePreset } from '../hooks/useDeliveredShipments';

type ViewMode = 'inflight' | 'exceptions' | 'complete';

interface StatsHeaderProps {
  stats: MarketStats | null;
  loading: boolean;
  viewMode: ViewMode;
  activeFilter?: ShipmentFilter;
  onViewModeChange: (mode: ViewMode) => void;
  onFilterChange?: (filter: ShipmentFilter) => void;
  onNavigateToComplete?: (preset: DateRangePreset) => void;
}

interface InflightCardProps {
  awaitingShip: number;
  transit0d: number;
  transit1d: number;
  transit2dPlus: number;
  atRisk: number;
  needsAction: number;
  loading?: boolean;
  isActive: boolean;
  onClick: () => void;
}

function InflightCard({
  awaitingShip,
  transit0d,
  transit1d,
  transit2dPlus,
  atRisk,
  needsAction,
  loading,
  isActive,
  onClick,
}: InflightCardProps) {
  if (loading) {
    return (
      <Paper p="md" radius="md" withBorder>
        <Skeleton height={20} width="40%" mb="xs" />
        <Skeleton height={24} width="90%" />
      </Paper>
    );
  }

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={{
        cursor: 'pointer',
        backgroundColor: isActive ? 'var(--mantine-color-blue-0)' : undefined,
        borderColor: isActive ? 'var(--mantine-color-blue-4)' : undefined,
      }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb="xs">
        Inflight
      </Text>
      <Group gap="lg">
        <Stack gap={2}>
          <Text size="lg" fw={700}>{awaitingShip}</Text>
          <Text size="xs" c="dimmed">await ship</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c="blue">{transit0d}</Text>
          <Text size="xs" c="dimmed">same day</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c="cyan">{transit1d}</Text>
          <Text size="xs" c="dimmed">1-day</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c={transit2dPlus > 0 ? 'orange' : undefined}>{transit2dPlus}</Text>
          <Text size="xs" c="dimmed">2-day+</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c={atRisk > 0 ? 'red' : undefined}>{atRisk}</Text>
          <Text size="xs" c="dimmed">at risk</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c={needsAction > 0 ? 'violet' : undefined}>{needsAction}</Text>
          <Text size="xs" c="dimmed">action</Text>
        </Stack>
      </Group>
    </Paper>
  );
}

interface DeliveredCardProps {
  today: number;
  yesterday: number;
  thisWeek: number;
  avgTransitHours: number | null;
  loading?: boolean;
  isActive: boolean;
  onClick: () => void;
}

function DeliveredCard({
  today,
  yesterday,
  thisWeek,
  avgTransitHours,
  loading,
  isActive,
  onClick,
}: DeliveredCardProps) {
  if (loading) {
    return (
      <Paper p="md" radius="md" withBorder>
        <Skeleton height={20} width="40%" mb="xs" />
        <Skeleton height={24} width="80%" />
      </Paper>
    );
  }

  // Format avg transit as hours:minutes or days if > 24h
  const formatTransit = (hours: number | null) => {
    if (hours === null || hours === 0) return '-';
    if (hours < 24) {
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      return `${h}h ${m}m`;
    }
    return `${(hours / 24).toFixed(1)}d`;
  };

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={{
        cursor: 'pointer',
        backgroundColor: isActive ? 'var(--mantine-color-green-0)' : undefined,
        borderColor: isActive ? 'var(--mantine-color-green-4)' : undefined,
      }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb="xs">
        Delivered
      </Text>
      <Group gap="lg">
        <Stack gap={2}>
          <Text size="lg" fw={700} c="green">{today}</Text>
          <Text size="xs" c="dimmed">today</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c="teal">{yesterday}</Text>
          <Text size="xs" c="dimmed">yesterday</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700} c="green">{thisWeek}</Text>
          <Text size="xs" c="dimmed">this week</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="lg" fw={700}>{formatTransit(avgTransitHours)}</Text>
          <Text size="xs" c="dimmed">avg transit</Text>
        </Stack>
      </Group>
    </Paper>
  );
}

export function StatsHeader({
  stats,
  loading,
  viewMode,
  onViewModeChange,
  onNavigateToComplete,
}: StatsHeaderProps) {
  const handleInflightClick = () => {
    onViewModeChange('inflight');
  };

  const handleExceptionsClick = () => {
    onViewModeChange('exceptions');
  };

  const handleDeliveredClick = () => {
    onViewModeChange('complete');
    if (onNavigateToComplete) {
      onNavigateToComplete('today');
    }
  };

  const exceptionsCount = stats?.exceptions ?? 0;

  return (
    <SimpleGrid cols={{ base: 1, md: exceptionsCount > 0 ? 3 : 2 }} spacing="md">
      <InflightCard
        awaitingShip={stats?.awaiting_fulfillment ?? 0}
        transit0d={stats?.transit_0d ?? 0}
        transit1d={stats?.transit_1d ?? 0}
        transit2dPlus={stats?.transit_2d_plus ?? 0}
        atRisk={stats?.at_risk ?? 0}
        needsAction={stats?.needs_action ?? 0}
        loading={loading}
        isActive={viewMode === 'inflight'}
        onClick={handleInflightClick}
      />
      <DeliveredCard
        today={stats?.delivered_today ?? 0}
        yesterday={stats?.delivered_yesterday ?? 0}
        thisWeek={stats?.delivered_this_week ?? 0}
        avgTransitHours={stats?.avg_transit_hours_7d ?? null}
        loading={loading}
        isActive={viewMode === 'complete'}
        onClick={handleDeliveredClick}
      />
      {exceptionsCount > 0 && (
        <Paper
          p="md"
          radius="md"
          withBorder
          onClick={handleExceptionsClick}
          style={{
            cursor: 'pointer',
            backgroundColor: viewMode === 'exceptions' ? 'var(--mantine-color-red-0)' : undefined,
            borderColor: viewMode === 'exceptions' ? 'var(--mantine-color-red-4)' : undefined,
          }}
        >
          <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb="xs">
            Exceptions
          </Text>
          <Group gap="lg">
            <Stack gap={2}>
              <Text size="lg" fw={700} c="red">{exceptionsCount}</Text>
              <Text size="xs" c="dimmed">open</Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </SimpleGrid>
  );
}
